import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, CalendarDays, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const fmtDate = (d) => d.toISOString().slice(0, 10);
const dayLabel = (d) => d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });

function buildDays(lookahead = 60) {
  const out = [];
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  for (let i = 1; i <= lookahead; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    if (d.getDay() === 0 || d.getDay() === 6) continue;
    out.push(d);
  }
  return out;
}

export default function BookConsultation() {
  const [config, setConfig] = useState({ slots: [], lookahead_days: 60 });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get("/consultations/config").then((r) => setConfig(r.data));
  }, []);

  const days = useMemo(() => buildDays(config.lookahead_days || 60), [config.lookahead_days]);

  useEffect(() => {
    if (!selectedDate) return;
    api.get(`/consultations/booked-slots?date=${selectedDate}`).then((r) => setBooked(r.data.booked || []));
  }, [selectedDate]);

  const pickDate = (d) => {
    const iso = fmtDate(d);
    setSelectedDate(iso);
    setSelectedSlot(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/consultations/book", { ...form, date: selectedDate, time: selectedSlot });
      setConfirmed({ ...form, date: selectedDate, time: selectedSlot });
      toast.success("Consultation confirmed! Check your email.");
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed. Try a different time.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="book-page" className="pt-32 min-h-screen">
      <SEO
        title="Book a Consultation"
        description="A relaxed 15-minute call with Elite Pixels — chat about your wedding, your story, and see if we're the right fit."
        path="/book"
      />

      <section className="section-pad pb-12 max-w-[1400px] mx-auto text-center">
        <div className="eyebrow">Say Hello</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light mt-4 tracking-tight">
          A 15-Minute <em className="text-[#D4AF37] not-italic">Chat</em>
        </h1>
        <p className="text-white/55 mt-4 max-w-2xl mx-auto">
          A relaxed call to talk about your wedding, what matters most to you, and see if we feel like the right fit. No pressure, no script.
        </p>
      </section>

      {/* Stepper */}
      <div className="max-w-4xl mx-auto px-6 mb-12 flex items-center justify-center gap-4">
        {["Pick a date", "Choose a time", "Confirm"].map((s, i) => (
          <div key={s} className="flex items-center gap-3">
            <div className={`w-8 h-8 flex items-center justify-center text-xs font-semibold ${step >= i + 1 ? "bg-[#D4AF37] text-black" : "border border-white/30 text-white/40"}`}>
              {i + 1}
            </div>
            <span className={`text-[0.7rem] tracking-[0.2em] uppercase ${step >= i + 1 ? "text-[#D4AF37]" : "text-white/40"}`}>{s}</span>
            {i < 2 && <span className="w-12 h-px bg-white/15 ml-2" />}
          </div>
        ))}
      </div>

      <section className="max-w-5xl mx-auto px-6 pb-24">
        {step === 3 && confirmed ? (
          <div className="bg-[#0a0a0a] border border-[#D4AF37]/40 p-12 text-center" data-testid="booking-confirmation">
            <CheckCircle2 size={56} className="text-[#D4AF37] mx-auto mb-6" />
            <h2 className="font-serif text-4xl">Looking Forward To Meeting You, {confirmed.name.split(" ")[0]}</h2>
            <p className="text-white/60 mt-3">A confirmation has been sent to <span className="text-[#D4AF37]">{confirmed.email}</span>.</p>
            <div className="mt-8 inline-flex items-center gap-6 border border-white/15 px-8 py-5">
              <div className="flex items-center gap-3"><CalendarDays className="text-[#D4AF37]" size={20} /><span className="font-serif text-xl">{confirmed.date}</span></div>
              <div className="w-px h-8 bg-white/15" />
              <div className="flex items-center gap-3"><Clock className="text-[#D4AF37]" size={20} /><span className="font-serif text-xl">{confirmed.time} Toronto</span></div>
            </div>
            <p className="text-white/50 mt-8 text-sm">We'll call you at the time above on the phone number you provided.</p>
            <a href="/" className="btn-ghost mt-10 inline-flex">Back to Home</a>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-[#0a0a0a] border border-white/10 p-6">
              <div className="eyebrow mb-4 flex items-center gap-2"><CalendarDays size={14}/> Available Weekdays</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[480px] overflow-y-auto scrollbar-hide">
                {days.map((d) => {
                  const iso = fmtDate(d);
                  const active = selectedDate === iso;
                  return (
                    <button key={iso} onClick={() => { pickDate(d); setStep(2); }} data-testid={`day-${iso}`}
                      className={`p-3 text-xs text-center transition ${active ? "bg-[#D4AF37] text-black" : "border border-white/15 hover:border-[#D4AF37]"}`}>
                      <div className="font-medium">{dayLabel(d)}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-[#0a0a0a] border border-white/10 p-6">
              {!selectedDate ? (
                <div className="h-full flex items-center justify-center text-white/40 text-sm">
                  Select a date to see available times.
                </div>
              ) : selectedSlot && step === 2 ? (
                <form onSubmit={submit} className="space-y-4" data-testid="book-form">
                  <div className="eyebrow mb-2 flex items-center gap-2"><Clock size={14}/>{selectedDate} · {selectedSlot} Toronto</div>
                  <button type="button" onClick={() => setSelectedSlot(null)} className="text-xs text-white/40 hover:text-[#D4AF37] mb-2">← Change time</button>
                  {[["name","Full Name *","text",true],["email","Email *","email",true],["phone","Phone *","tel",true]].map(([k,l,t,r])=>(
                    <input key={k} type={t} required={r} placeholder={l} value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})}
                      data-testid={`book-${k}`}
                      className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none"/>
                  ))}
                  <textarea placeholder="Anything you'd like us to know about your wedding (optional)" rows={4} value={form.message} onChange={e=>setForm({...form,message:e.target.value})}
                    data-testid="book-message"
                    className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none"/>
                  <button type="submit" disabled={busy} className="btn-gold w-full" data-testid="book-submit">
                    {busy ? "Confirming..." : "Confirm Our Chat"} <ArrowRight size={14}/>
                  </button>
                </form>
              ) : (
                <>
                  <div className="eyebrow mb-4 flex items-center gap-2"><Clock size={14}/> Times for {selectedDate}</div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {config.slots.map((s) => {
                      const isBooked = booked.includes(s);
                      const active = selectedSlot === s;
                      return (
                        <button key={s} disabled={isBooked} onClick={() => setSelectedSlot(s)} data-testid={`slot-${s}`}
                          className={`p-3 text-sm transition ${isBooked ? "border border-white/5 text-white/20 line-through cursor-not-allowed" : active ? "bg-[#D4AF37] text-black" : "border border-white/15 hover:border-[#D4AF37]"}`}>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-white/40 mt-6">All times Toronto (America/Toronto). Booked slots are disabled.</p>
                </>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
